import React, { useMemo } from 'react';
import { useBreakpoint, useBreakpointValue } from '@landr/maestro';
import Wrapper from 'components/CommonUi/Wrapper';
import GenericCarouselContext from './GenericCarouselContext';
import { Container, GenericCarouselStyled } from './GenericCarousel.styled';
import { getCardItems } from './GenericCarousel.helpers';
import { GenericCarouselProps } from './GenericCarousel.type';
import { Header } from './components';

const DefaultImageHeight = 192;

function _GenericCarousel<P>({
    id = '',
    title,
    items = [],
    cardComponent: CardComponent,
    fallbackComponent: FallbackComponent,
    isFetching,
    imageHeight = DefaultImageHeight,
    carouselUrl,
    showMoreCard,
    linkLocation = 'Title',
    carouselGroupIndex,
    slidesPerPage,
    showHeader = true,
    observerComponent,
    noMoreItems = false,
    containerRef,
    ...props
}: GenericCarouselProps<P>) {
    const isMobile = useBreakpoint('md');

    let numberOfSlidesPerPage: any;

    if (typeof slidesPerPage === 'number') {
        numberOfSlidesPerPage = slidesPerPage;
    } else {
        numberOfSlidesPerPage = useBreakpointValue<number>(slidesPerPage);
    }

    // SKELETON
    const skeletonData = useMemo((): Array<React.ReactElement> => {
        const dataPlaceholder: Array<React.ReactElement> = [];

        for (let i = 0; i < numberOfSlidesPerPage; i++) {
            dataPlaceholder.push(<FallbackComponent key={`fallback ${i}`} />);
        }

        return dataPlaceholder;
    }, [FallbackComponent, numberOfSlidesPerPage]);

    // ITEMS
    const cardItems = getCardItems(
        items,
        CardComponent,
        isFetching,
        noMoreItems,
        skeletonData,
        FallbackComponent,
        observerComponent,
        showMoreCard,
    );

    return (
        <Container
            ref={containerRef}
            data-carousel-group-index={
                typeof carouselGroupIndex === 'number'
                    ? carouselGroupIndex
                    : null
            }
        >
            <GenericCarouselContext.Provider
                value={{
                    carouselId: id,
                    carouselName: title,
                    carouselUrl: carouselUrl,
                    linkLocation: linkLocation,
                }}
            >
                {showHeader && <Header />}

                <Wrapper isNegative>
                    <GenericCarouselStyled
                        items={cardItems}
                        slidesPerPage={slidesPerPage}
                        itemsOffset={isMobile ? 'xl' : undefined}
                        {...props}
                    />
                </Wrapper>
            </GenericCarouselContext.Provider>
        </Container>
    );
}

export const GenericCarousel = React.memo(_GenericCarousel);

export default GenericCarousel;
